<script setup lang="ts">
import UserMenu from '@/components/user/index.vue';
import QuickAddMenu from '@/components/QuickAddMenu/QuickAddMenu.vue';
import GlobalSearch from '@/components/GlobalSearch/GlobalSearch.vue';
import useGlobalStore from '@/store/globalStore';
import useMatestoreStore from '@/store/matestoreStore';
import MicroAppItem from '@/components/LayoutV2/MicroAppItem.vue';
import UiMicroAppItem from '@/components/LayoutV2/UiMicroAppItem.vue';
import MateLogo from '@/components/MateLogo/MateLogo.vue';
import AllMicroAppDrawer from '@/components/LayoutV2/AllMicroAppDrawer.vue';
import { nextTick, onMounted, ref, watch } from 'vue';
import { debounce } from 'lodash-es';
import NoticePopover from '@/components/NoticePopover/NoticePopover.vue';
import { useMicroAppItemActions } from '@/components/LayoutV2/useMicroAppItemAction';

const globalStore = useGlobalStore();
const microAppStore = useMatestoreStore();
const headerAreaRef = ref<HTMLElement | null>(null);
const bottomAreaRef = ref<HTMLElement | null>(null);
const { isMicroAppActivated } = useMicroAppItemActions();
const calculateListHeight = () => {
  if (headerAreaRef.value && bottomAreaRef.value) {
    const headerHeight = headerAreaRef.value.clientHeight;
    const bottomHeight = bottomAreaRef.value.clientHeight;
    return `calc(100vh - ${headerHeight + bottomHeight}px)`;
  }
  return 'calc(100vh - 240px)';
};

const listHeight = ref<string>(calculateListHeight());

const allMicroAppDrawer = ref<InstanceType<typeof AllMicroAppDrawer> | null>(
  null
);
const handleToggleVisible = () => {
  globalStore.toggleSidebarVisibility();
};

onMounted(() => {
  listHeight.value = calculateListHeight();
  const debouncedCalculateListHeight = debounce(() => {
    listHeight.value = calculateListHeight();
  }, 20);
  window.onresize = () => {
    debouncedCalculateListHeight();
  };
});

watch(
  () => globalStore?.sidebarVisible,
  () => {
    nextTick(() => {
      setTimeout(() => {
        listHeight.value = calculateListHeight();
      }, 100);
    });
  }
);
</script>

<template>
  <div
    :class="{
      'layout-v2-sidebar': true,
      'layout-v2-sidebar--collapsed': globalStore.sidebarVisible === false,
    }"
  >
    <div id="platformFunctionEntry" ref="headerAreaRef" class="header-area">
      <div class="logo">
        <mate-logo :size="globalStore.sidebarVisible ? 'medium' : 'mini'" />
      </div>
      <div class="action-list">
        <UserMenu class="avatar" theme="v2" />
        <div class="action-list__right">
          <notice-popover theme="v2" />
          <quick-add-menu theme="v2" />
        </div>
      </div>
      <div class="global-search-wrapper">
        <global-search
          class="layout-v2-search"
          :size="globalStore.sidebarVisible ? 'middle' : 'mini'"
          position="right"
        />
      </div>
    </div>
    <div
      class="microapp-list"
      :style="{
        height: listHeight,
      }"
    >
      <ui-micro-app-item
        title="全部应用"
        icon="matestore_filled"
        :size="globalStore.sidebarVisible ? 'middle' : 'mini'"
        @click="allMicroAppDrawer?.openDrawer()"
      />
      <micro-app-item
        v-for="(item, index) in microAppStore.frequentlyZoneMicroApps"
        :id="index === 0 ? 'sidebarMoreFunction' : undefined"
        :key="item.name"
        :title="item.microDataRef.label"
        :icon="item.microDataRef.icon"
        :badge="item.microDataRef.badgeNum"
        type="frequently"
        :micro-app-name="item.name"
        v-show="!item.microDataRef.hideOnMenu"
        :activated="isMicroAppActivated(item.name)"
        :brand="item.microDataRef.brand"
        :size="globalStore.sidebarVisible ? 'middle' : 'mini'"
      />
      <n-divider class="microapp-divider" />
      <micro-app-item
        v-for="item in microAppStore.temporaryZoneMicroApps"
        :key="item.name"
        :title="item.microDataRef.label"
        :icon="item.microDataRef.icon"
        :badge="item.microDataRef.badgeNum"
        type="temporary"
        v-show="!item.microDataRef.hideOnMenu"
        :micro-app-name="item.name"
        :activated="isMicroAppActivated(item.name)"
        :brand="item.microDataRef.brand"
        :size="globalStore.sidebarVisible ? 'middle' : 'mini'"
      />
    </div>
    <div class="bottom-area" ref="bottomAreaRef">
      <n-button
        id="switchSidebarCollapse"
        class="mate-btn-reverse-icon"
        type="text"
        v-tit-tracker:click="{
          button_group: '侧边栏',
          button_name: globalStore.sidebarVisible ? '展开' : '收起',
          trackName: 'FrameClick',
          requirement_id: 754,
          track_sign: 'aplatform.b2862.c2866.click.FrameClick',
        }"
        :icon="globalStore.sidebarVisible ? 'fold' : 'open'"
        @click="handleToggleVisible"
      ></n-button>
    </div>
    <all-micro-app-drawer ref="allMicroAppDrawer" />
  </div>
</template>

<style scoped lang="scss">
.layout-v2-sidebar {
  height: 100vh;
  background: var(--tit-sidebar-bg);
  .action-list {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 13px 8px;
    .action-list__right {
      display: flex;
      align-items: center;
    }
  }

  :deep(.mate-btn-reverse-icon) {
    color: var(--tit-sidebar-menu-label-color);
    .n-icon {
      font-size: 20px !important;
    }
    &:hover {
      background: #9c9fac26 !important;
    }
    &:active {
      background: #00b3be14 !important;
    }
  }

  :deep(.n-divider--horizontal) {
    border-color: var(--tit-sidebar-menu-divider);
  }

  &--collapsed {
    .action-list {
      flex-direction: column;
      padding: 0px 8px;
    }
    .action-list__right {
      flex-direction: column;
    }

    .avatar {
      margin-bottom: 12px;
    }
    :deep(.mate-btn-reverse-icon) {
      margin-bottom: 12px;
      border-radius: 32px;
      background: var(--tit-sidebar-icon-bg);
      width: 32px;
      height: 32px;
      display: flex;
      align-items: center;
    }
    .bottom-area {
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }
}

.global-search-wrapper {
  padding: 0px 8px;
}
.header-area {
  overflow: hidden;
  padding-bottom: 12px;
  border-bottom: 1px solid var(--tit-sidebar-menu-divider);
}

.bottom-area {
  padding: 8px 12px;
  display: flex;
  align-items: center;
  border-top: 1px solid var(--tit-sidebar-menu-divider);

  :deep(.mate-btn-reverse-icon) {
    border-radius: 4px;
    background: transparent;
    margin-bottom: 0;
  }
}
.microapp-list {
  height: calc(100vh - 240px);
  overflow: auto;
  scrollbar-width: thin;
  scrollbar-color: #b2b2b2 transparent;
  padding: 12px 0;
}
.microapp-divider {
  margin-left: 8px;
  margin-right: 8px;
  width: auto;
}
.global-search-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
}
.layout-v2-search {
  border: 1px solid rgba(255, 255, 255, 0.5);
}
.layout-v2-sidebar--collapsed {
  .layout-v2-search {
    border: 0;
  }
}
</style>
